import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import {
    map,
    catchError,
    switchMap,
    exhaustMap,
    filter,
    withLatestFrom,
} from 'rxjs/operators';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';

import { common } from '@qwyk/models';

import * as fromCustomViews from './custom-views-ui.reducer';
import { CustomViewsUiActions } from './custom-views-ui.actions';
import * as CustomViewsUiSelectors from './custom-views-ui.selectors';
import { CustomViewsUiService } from '../custom-views-ui.service';

@Injectable()
export class CustomViewsUiEffects {
    private readonly DEFAULT_VIEW_ID = 'default';

    createCustomView$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomViewsUiActions.createCustomView),
            switchMap(action =>
                this.service
                    .createCustomView(action.parent, action.request)
                    .pipe(
                        map(customView =>
                            CustomViewsUiActions.createCustomViewSuccess({
                                custom_view: customView,
                            })
                        )
                    )
            ),
            catchError(error => {
                return of(
                    CustomViewsUiActions.createCustomViewFailure({ error })
                );
            })
        )
    );

    updateCustomView$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomViewsUiActions.updateCustomView),
            exhaustMap(action =>
                this.service
                    .updateCustomView(action.parent, action.request)
                    .pipe(
                        map(customView =>
                            CustomViewsUiActions.updateCustomViewSuccess({
                                custom_view: customView,
                                shouldRefreshTable: action.shouldRefreshTable,
                            })
                        )
                    )
            ),
            catchError(error => {
                return of(
                    CustomViewsUiActions.updateCustomViewFailure({ error })
                );
            })
        )
    );

    setSelectedView$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomViewsUiActions.setSelectedView),
            filter(
                (
                    action
                ): action is typeof action & {
                    payload: common.CustomView & { parent: string };
                } => !!action.payload?.parent
            ),
            withLatestFrom(
                this.store.select(CustomViewsUiSelectors.getAllCustomViews)
            ),
            switchMap(([action, customViews]) => {
                if (action.payload.uid === this.DEFAULT_VIEW_ID) {
                    const currentView = customViews.find(
                        view =>
                            view.uid !== this.DEFAULT_VIEW_ID && view.is_current
                    );

                    if (!currentView) {
                        return of(
                            CustomViewsUiActions.updateCustomViewSuccess({
                                custom_view: {
                                    ...action.payload,
                                    is_current: true,
                                },
                                shouldRefreshTable: true,
                            })
                        );
                    }

                    return this.service
                        .updateCustomView(action.payload.parent, {
                            ...currentView,
                            is_current: false,
                        })
                        .pipe(
                            map(() =>
                                CustomViewsUiActions.updateCustomViewSuccess({
                                    custom_view: {
                                        ...action.payload,
                                        is_current: true,
                                    },
                                    shouldRefreshTable: true,
                                })
                            ),
                            catchError(error =>
                                of(
                                    CustomViewsUiActions.updateCustomViewFailure(
                                        {
                                            error,
                                        }
                                    )
                                )
                            )
                        );
                }
                return this.service
                    .updateCustomView(action.payload.parent, {
                        ...action.payload,
                        is_current: true,
                    })
                    .pipe(
                        map(customView => {
                            const custom_view =
                                !customView || Array.isArray(customView)
                                    ? { ...action.payload, is_current: true }
                                    : customView;
                            return CustomViewsUiActions.updateCustomViewSuccess(
                                {
                                    custom_view,
                                    shouldRefreshTable: true,
                                }
                            );
                        }),
                        catchError(error =>
                            of(
                                CustomViewsUiActions.updateCustomViewFailure({
                                    error,
                                })
                            )
                        )
                    );
            }),
            catchError(error => {
                return of(
                    CustomViewsUiActions.updateCustomViewFailure({ error })
                );
            })
        )
    );

    deleteCustomView$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomViewsUiActions.deleteCustomView),
            switchMap(action =>
                this.service
                    .deleteCustomView(action.parent, action.request)
                    .pipe(
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        map(_ => {
                            return CustomViewsUiActions.deleteCustomViewSuccess(
                                {
                                    custom_view: action.request,
                                }
                            );
                        })
                    )
            ),
            catchError(error => {
                return of(
                    CustomViewsUiActions.deleteCustomViewFailure({ error })
                );
            })
        )
    );

    constructor(
        private actions$: Actions,
        private service: CustomViewsUiService,
        private store: Store<fromCustomViews.CustomViewsState>
    ) {}
}
